// Iter108 — Shared company-logo component.
//
// Read-only, in-memory only.  Uses the Financial Modeling Prep
// free image CDN (`https://financialmodelingprep.com/image-stock/
// {SYMBOL}.png`).  No API key required.  On 404 / load failure
// the component falls back to a clean monogram badge so we never
// show a broken-image icon in the operator UI.
//
// Hard contract:
//   * Frontend only.
//   * No persistence (no localStorage / cookies).  An in-memory
//     `Map` caches the per-symbol load state so we don't re-flash
//     the fallback while React re-renders.
//   * No backend call.
//   * No tracking / no analytics.
//   * No fabricated company data — if the CDN returns 404 we
//     render the monogram, not a placeholder ticker.
import React from "react";

// In-memory load-state cache.  Keys: uppercase symbol.  Values:
//   "loading" | "ok" | "err"
const _LOGO_STATE = new Map();

function _fmpLogoUrl(symbol) {
  const s = String(symbol || "").trim().toUpperCase();
  if (!s) return null;
  return `https://financialmodelingprep.com/image-stock/${encodeURIComponent(s)}.png`;
}

function _monogram(symbol) {
  const s = String(symbol || "").trim().toUpperCase();
  if (!s) return "";
  // For 1-3 char tickers, use the whole ticker; otherwise first 2.
  return s.length <= 3 ? s : s.slice(0, 2);
}

export function CompanyLogo({ symbol, size = 22, testid = "company-logo" }) {
  const sym = String(symbol || "").trim().toUpperCase();
  const initialState = _LOGO_STATE.get(sym) || "loading";
  const [state, setState] = React.useState(initialState);

  React.useEffect(() => {
    if (_LOGO_STATE.get(sym) === "err" || _LOGO_STATE.get(sym) === "ok") {
      setState(_LOGO_STATE.get(sym));
    } else {
      setState("loading");
    }
  }, [sym]);

  const url = _fmpLogoUrl(sym);
  const dim = `${size}px`;
  const wrapper = {
    width: dim, height: dim, minWidth: dim, minHeight: dim,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    borderRadius: "4px", overflow: "hidden",
    background: "#1A1A1F",
    border: "1px solid #26262B",
    flexShrink: 0,
  };

  if (!sym || state === "err" || !url) {
    return (
      <span
        data-testid={`${testid}-fallback-${sym || "unknown"}`}
        style={{
          ...wrapper,
          fontFamily: "ui-monospace, Menlo, Consolas, monospace",
          fontWeight: 700,
          fontSize: `${Math.max(8, Math.round(size * 0.42))}px`,
          color: "#22D3FF",
          letterSpacing: "0.05em",
        }}
      >
        {_monogram(sym)}
      </span>
    );
  }

  return (
    <span style={wrapper} data-testid={`${testid}-${sym}`}>
      <img
        src={url}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        style={{
          width: dim, height: dim, objectFit: "contain",
          background: "#fff",
        }}
        onLoad={() => {
          _LOGO_STATE.set(sym, "ok");
          if (state !== "ok") setState("ok");
        }}
        onError={() => {
          _LOGO_STATE.set(sym, "err");
          setState("err");
        }}
      />
    </span>
  );
}

export default CompanyLogo;
